import EmailViewHeader from "@/components/mailsList/EmailViewHeader";
import { MailView } from "@/components/toggles/MailViewToggle";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import CustomView from "@/domain/entities/customViews/CustomView";
import Email, { MailsListId } from "@/domain/entities/emails/Email";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import EmailDisplayMode from "@/domain/enums/EmailDisplayMode";
import SmartLabel from "@/domain/enums/SmartLabel";
import useSttExtractFromEmail from "@/hooks/adapters/useSttExtractFromEmail";
import useSetMailEditor from "@/hooks/useSetMailEditor";
import useSuggestion from "@/hooks/useSuggestion";
import useWindowWidth from "@/hooks/utils/useWindowWidth";
import { queryKeys } from "@/lib/queryKeys";
import { cn } from "@/lib/utils";
import deepFocusStore from "@/stores/deepFocusStore";
import mailStore, {
    clearOpenMailIntent,
    setOpenedMail,
} from "@/stores/mailStore";
import { useQueryClient } from "@tanstack/react-query";
import { shallow, useSelector } from "@tanstack/react-store";
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import DeepFocusSelect from "../deepFocus/DeepFocusSelect";
import { ContextBarVariant } from "../mailThread/ContextBar";
import MailThread from "../mailThread/MailThread";
import Spinner from "../ui/spinner";
import CustomViewWidgets from "../widgets/CustomViewWidgets";
import EmailList from "./EmailList";
import EmailMosaic from "./EmailMosaic";
import EmailViewSkeleton from "./EmailViewSkeleton";
import { FocusTarget } from "./MailAction";
import NoEmails from "./NoEmails";

const INFINITE_SCROLL_THRESHOLD = 1000;

export enum EmailViewVariant {
    DEFAULT,
    VISTASSISTANT,
}

type EmailViewProps = {
    mails?: Email[];
    loadNextPage?: () => void;
    isLoading: boolean;
    title?: ReactNode;
    displayMode: EmailDisplayMode;
    customView?: CustomView;
    variant?: EmailViewVariant;
    className?: string;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    listId: MailsListId;
    updateVersion?: number;
    isAwaitingMails?: boolean;
    isViewConfiguring?: boolean;
};

const EmailView = ({
    mails,
    title,
    loadNextPage,
    isLoading,
    displayMode,
    customView,
    variant = EmailViewVariant.DEFAULT,
    className,
    isFetchingNextPage,
    hasNextPage,
    listId,
    updateVersion,
    isAwaitingMails = false,
    isViewConfiguring = false,
}: EmailViewProps) => {
    const { isVistassistantResult } = useVistassistantMail();
    const queryClient = useQueryClient();
    const extractFromEmail = useSttExtractFromEmail();

    const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
    const { editor, suggestionEditor } = useSetMailEditor(setAttachments);
    const [pendingSuggestion, setPendingSuggestion] = useState<boolean>(false);

    const [selectedMails, setSelectedMails] = useState<Email[]>([]);
    const [mailInputExpanded, setMailInputExpanded] = useState<boolean>(false);
    const [previousDisplayView, setPreviousDisplayView] = useState<MailView>(
        displayMode === EmailDisplayMode.MOSAIC ? MailView.GRID : MailView.LIST,
    );
    const [currentDisplayView, setCurrentDisplayView] = useState<MailView>(
        displayMode === EmailDisplayMode.MOSAIC ? MailView.GRID : MailView.LIST,
    );

    const toInputRef = useRef<HTMLInputElement>(null);
    const toInputDeepFocusRef = useRef<HTMLInputElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const openedMailRef = useRef<HTMLDivElement>(null);
    const isListIdChanging = useRef<boolean>(false);
    const pendingScrollMailId = useRef<string | undefined>(undefined);
    const isScrollToOpenedMailPending = useRef(false);

    const openedMail = useSelector(mailStore, (state) => state.openedMail, {
        compare: shallow,
    });
    const openMailIntent = useSelector(
        mailStore,
        (state) => state.openMailIntent,
    );
    const isDeepFocusActive = useSelector(
        deepFocusStore,
        (state) => state.isActive,
    );

    const windowWidth = useWindowWidth();
    const isNarrowForSideView = windowWidth < 1152;

    const {
        acceptOrRefuseSuggestion,
        isLoading: isLoadingSuggestion,
        isPendingChange,
        makeSuggestion,
    } = useSuggestion({
        editor,
        suggestionEditor,
        setPendingSuggestion,
        mailContent: openedMail?.message ?? "",
    });

    const handleExpandMailInput = (
        inputExpanded: boolean,
        focusTarget: FocusTarget = FocusTarget.EDITOR,
        delay: number = 0,
    ) => {
        setMailInputExpanded(inputExpanded);
        if (pendingSuggestion) {
            acceptOrRefuseSuggestion(false);
        }
        if (inputExpanded && focusTarget === FocusTarget.EDITOR) {
            setTimeout(() => {
                editor?.commands.focus();
            }, delay);
        } else if (inputExpanded && focusTarget === FocusTarget.TO_INPUT) {
            setTimeout(() => {
                if (isDeepFocusActive) {
                    toInputDeepFocusRef.current?.focus();
                } else {
                    toInputRef.current?.focus();
                }
            }, delay);
        }
    };

    // La bascule du tri finit plusieurs secondes après le clic : son handler lit l'état courant, pas celui du clic
    const currentStateRef = useRef({ listId, currentDisplayView, openedMail });
    useLayoutEffect(() => {
        currentStateRef.current = { listId, currentDisplayView, openedMail };
    });

    // Bascule du tri du Focus : la liste se recharge seule (événement du SDK) et la sélection est vidée. En liste
    // latérale, le mail ouvert reste ouvert (seul l'ordre change) et on le garde à l'écran ; sinon retour en haut.
    // Rien si on a quitté le Focus pendant la bascule
    const handleFocusSortModeChange = () => {
        const current = currentStateRef.current;
        if (current.listId !== SmartLabel.Focus) return;

        setSelectedMails([]);
        if (
            current.currentDisplayView === MailView.SIDE_LIST &&
            current.openedMail
        ) {
            isScrollToOpenedMailPending.current = true;
            queryClient
                .refetchQueries(
                    {
                        queryKey: queryKeys.mails.list(current.listId),
                        type: "active",
                    },
                    { cancelRefetch: false },
                )
                .finally(() =>
                    setTimeout(() => {
                        isScrollToOpenedMailPending.current = false;
                    }),
                );
        } else if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    };

    useLayoutEffect(() => {
        if (!isScrollToOpenedMailPending.current) return;
        isScrollToOpenedMailPending.current = false;
        openedMailRef.current?.scrollIntoView({ block: "start" });
    }, [mails]);

    const defaultDisplayView =
        displayMode === EmailDisplayMode.MOSAIC ? MailView.GRID : MailView.LIST;

    useEffect(() => {
        isListIdChanging.current = true;
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
        setSelectedMails([]);
        // Only the main list owns the global openedMail; the assistant list
        // must not clear it when its own listId changes (new search).
        if (!isVistassistantResult) {
            setOpenedMail(undefined);
        }
        setPreviousDisplayView(defaultDisplayView);
        setCurrentDisplayView(defaultDisplayView);

        // Wait for the render to complete
        setTimeout(() => {
            isListIdChanging.current = false;
        }, 0);
    }, [listId, displayMode]);

    useLayoutEffect(() => {
        if (currentDisplayView === MailView.SIDE_LIST) {
            if (openedMail && openedMailRef.current && scrollRef.current) {
                openedMailRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        } else if (pendingScrollMailId.current && scrollRef.current) {
            const mailId = pendingScrollMailId.current;
            pendingScrollMailId.current = undefined;
            const el = scrollRef.current.querySelector<HTMLElement>(
                `[data-mail-id="${mailId}"]`,
            );
            if (el) {
                scrollRef.current.scrollTop = el.offsetTop;
            }
        }
    }, [currentDisplayView]);

    const handleSelectAllChange = (checked: boolean) => {
        if (!mails) return;

        setSelectedMails(checked ? mails : []);
    };

    const handleSelectChange = (checked: boolean, selected: Email) => {
        setSelectedMails((prev) =>
            checked
                ? [...prev, selected]
                : prev.filter((mail) => mail !== selected),
        );
    };

    const handleScroll = () => {
        if (!loadNextPage) return;

        const container = scrollRef.current;
        if (container) {
            const nearBottom =
                container.scrollHeight -
                    container.scrollTop -
                    container.clientHeight <
                INFINITE_SCROLL_THRESHOLD;

            if (nearBottom && !isFetchingNextPage && hasNextPage) {
                loadNextPage();
            }
        }
    };

    const resetOpenedMail = () => {
        setOpenedMail(undefined);
        setCurrentDisplayView(previousDisplayView);
    };

    useEffect(() => {
        // The assistant's result list shares this component but drives its own
        // selection via onMailClick (selectedMail), not the global openedMail.
        // Without this guard it would react to a mail opened elsewhere (e.g.
        // Focus) and overwrite openedMail with one of its own mails — opening
        // the wrong thread. See VistassistantMailContext.
        if (isVistassistantResult) return;
        if (!openedMail || isListIdChanging.current || !openMailIntent) return;

        // Apprentissage du dictionnaire STT depuis le mail ouvert (best-effort)
        extractFromEmail(openedMail);
        if (!isNarrowForSideView && currentDisplayView !== MailView.SIDE_LIST) {
            setPreviousDisplayView(currentDisplayView);
            setCurrentDisplayView(MailView.SIDE_LIST);
        }
        clearOpenMailIntent();
    }, [openedMail, openMailIntent]);

    useEffect(() => {
        if (isNarrowForSideView && currentDisplayView === MailView.SIDE_LIST) {
            setCurrentDisplayView(previousDisplayView);
        }
    }, [isNarrowForSideView]);

    // Items heights differ between list and grid views, so pixel-based scroll
    // restoration would land on the wrong period. Instead we anchor on the first
    // fully-visible mail ID before the switch, then scroll to that same element
    // after re-render — using useLayoutEffect so it runs before the browser paints.
    const getTopmostVisibleMailId = (): string | undefined => {
        if (!scrollRef.current) return undefined;
        const { scrollTop } = scrollRef.current;
        const item = Array.from(
            scrollRef.current.querySelectorAll<HTMLElement>("[data-mail-id]"),
        ).find((el) => el.offsetTop >= scrollTop);
        return item?.getAttribute("data-mail-id") ?? undefined;
    };

    const handleViewChange = (view: MailView) => {
        if (view === MailView.SIDE_LIST && isNarrowForSideView) return;
        pendingScrollMailId.current = getTopmostVisibleMailId();
        setCurrentDisplayView(view);
        if (view === MailView.SIDE_LIST) {
            if (!openedMail && mails && mails.length > 0) {
                setOpenedMail(mails[0]);
            }
        } else if (openedMail) {
            setOpenedMail(undefined);
        }
    };

    return (
        <div
            className={cn(
                "relative flex h-full w-full min-w-0 pb-2",
                className,
            )}
        >
            <div
                className={cn(
                    "flex h-full min-h-0 flex-1 flex-col overflow-hidden transition-all duration-500",
                    currentDisplayView === MailView.SIDE_LIST
                        ? "w-60 min-w-60 shrink xl:w-78 xl:min-w-78"
                        : "w-full min-w-40 xl:min-w-78",
                )}
            >
                <div className="flex w-full min-w-0 items-start justify-between">
                    <div className="min-w-0 flex-1">
                        <EmailViewHeader
                            hideSideListMode={
                                variant === EmailViewVariant.VISTASSISTANT ||
                                isNarrowForSideView
                            }
                            listId={listId}
                            mailsListFlattened={mails || []}
                            onFocusSortModeChange={handleFocusSortModeChange}
                            onSelectAllChange={handleSelectAllChange}
                            onViewChange={handleViewChange}
                            selectedMails={selectedMails}
                            setSelectedMails={setSelectedMails}
                            title={title}
                            view={currentDisplayView}
                        />
                    </div>
                    {listId === SmartLabel.Focus &&
                        !isNarrowForSideView &&
                        editor &&
                        suggestionEditor &&
                        loadNextPage &&
                        mails &&
                        mails.length > 0 && (
                            <DeepFocusSelect
                                acceptOrRefuseSuggestion={
                                    acceptOrRefuseSuggestion
                                }
                                attachments={attachments}
                                editor={editor}
                                hasNextPage={hasNextPage}
                                isFetchingNextPage={isFetchingNextPage}
                                isLoading={isLoading}
                                isLoadingSuggestion={isLoadingSuggestion}
                                isPendingChange={isPendingChange}
                                loadNextPage={loadNextPage}
                                mailInputExpanded={mailInputExpanded}
                                mails={mails}
                                makeSuggestion={makeSuggestion}
                                pendingSuggestion={pendingSuggestion}
                                setAttachments={setAttachments}
                                setMailInputExpanded={handleExpandMailInput}
                                setPendingSuggestion={setPendingSuggestion}
                                sideListMode={
                                    currentDisplayView === MailView.SIDE_LIST
                                }
                                suggestionEditor={suggestionEditor}
                                toInputRef={toInputDeepFocusRef}
                            />
                        )}
                </div>
                {isLoading || isAwaitingMails ? (
                    <ScrollArea className="relative min-h-0 flex-1 w-full pr-3.5">
                        <EmailViewSkeleton
                            mosaicColumns={
                                variant === EmailViewVariant.VISTASSISTANT
                                    ? 2
                                    : undefined
                            }
                            showCustomViewMessage={isViewConfiguring}
                            view={currentDisplayView}
                        />
                    </ScrollArea>
                ) : !mails || mails.length === 0 ? (
                    <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4">
                        <NoEmails />
                    </div>
                ) : (
                    <ScrollArea
                        className="relative min-h-0 flex-1 w-full pr-3.5"
                        onScroll={handleScroll}
                        scrollRef={scrollRef}
                    >
                        {currentDisplayView === MailView.LIST ||
                        currentDisplayView === MailView.SIDE_LIST ? (
                            <EmailList
                                handleCheckboxSelectChange={handleSelectChange}
                                listId={listId}
                                mailsListFlattened={mails}
                                openedMail={openedMail}
                                openedMailRef={openedMailRef}
                                selectedMails={selectedMails}
                                setMailInputExpanded={(
                                    inputExpanded,
                                    focusTarget,
                                ) =>
                                    handleExpandMailInput(
                                        inputExpanded,
                                        focusTarget,
                                        openedMail ? 0 : 1500, // delay to wait for the mail to be opened before setting the focus
                                    )
                                }
                                setSelectedMails={setSelectedMails}
                                squeezeItems={
                                    currentDisplayView === MailView.SIDE_LIST
                                }
                            />
                        ) : (
                            <EmailMosaic
                                columns={
                                    variant === EmailViewVariant.VISTASSISTANT
                                        ? 2
                                        : undefined
                                }
                                handleCheckboxSelectChange={handleSelectChange}
                                listId={listId}
                                mailsListFlattened={mails}
                                selectedMails={selectedMails}
                                setMailInputExpanded={(
                                    inputExpanded,
                                    focusTarget,
                                ) =>
                                    handleExpandMailInput(
                                        inputExpanded,
                                        focusTarget,
                                        openedMail ? 0 : 1500, // delay to wait for the mail to be opened before setting the focus
                                    )
                                }
                                setSelectedMails={setSelectedMails}
                                updateVersion={updateVersion}
                            />
                        )}
                        {isFetchingNextPage && (
                            <div className="absolute bottom-0 right-0 mb-5 mr-8 h-10 w-10">
                                <Spinner />
                            </div>
                        )}
                    </ScrollArea>
                )}
            </div>
            {customView &&
                !(currentDisplayView === MailView.SIDE_LIST && openedMail) &&
                !(isNarrowForSideView && openedMail) && (
                    <div className="w-85">
                        <CustomViewWidgets customView={customView} />
                    </div>
                )}
            {/* Même replié, ce fil reprendrait au Deep Focus l'éditeur de réponse qu'ils partagent */}
            {!isVistassistantResult &&
                !isDeepFocusActive &&
                editor &&
                suggestionEditor && (
                    <MailThread
                        acceptOrRefuseSuggestion={acceptOrRefuseSuggestion}
                        attachments={attachments}
                        classname={cn(
                            "bg-surface-background transition-all duration-500 relative",
                            (currentDisplayView === MailView.SIDE_LIST &&
                                openedMail) ||
                                (isNarrowForSideView && openedMail)
                                ? "w-full"
                                : "w-0 invisible",
                            variant === EmailViewVariant.VISTASSISTANT
                                ? "absolute bottom-0 right-0 top-0 left-0 z-modal-backdrop"
                                : "",
                            isNarrowForSideView &&
                                openedMail &&
                                variant !== EmailViewVariant.VISTASSISTANT &&
                                "absolute inset-0 z-10",
                        )}
                        editor={editor}
                        isLoading={isLoadingSuggestion}
                        isPendingChange={isPendingChange}
                        listId={listId}
                        mail={openedMail}
                        mailInputExpanded={mailInputExpanded}
                        makeSuggestion={makeSuggestion}
                        onClose={resetOpenedMail}
                        pendingSuggestion={pendingSuggestion}
                        setAttachments={setAttachments}
                        setMailInputExpanded={handleExpandMailInput}
                        setPendingSuggestion={setPendingSuggestion}
                        setSelectedMails={setSelectedMails}
                        suggestionEditor={suggestionEditor}
                        toInputRef={toInputRef}
                        variant={ContextBarVariant.SQUEEZE}
                    />
                )}
        </div>
    );
};

export default EmailView;
